import { supabase } from "@foundation/altzero_base/base/utils/supabaseClient";
import type {
  ChatThreadRecord,
  ChatMessageRecord,
  CreateThreadParams,
  CreateMessageParams,
  ChatProvider,
} from "../types/chat.types";

// Helper: query the charlie schema (default supabase client uses 'altzero' schema)
const charlie = () => supabase.schema("charlie");

// List threads for the current user, ordered by most recently updated
export const listThreads = async (
  userId: string,
  includeArchived = false
): Promise<ChatThreadRecord[]> => {
  let query = charlie()
    .from("chat_threads")
    .select("*")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false });

  if (!includeArchived) {
    query = query.eq("archived", false);
  }

  const { data, error } = await query;
  if (error) {
    console.error("Failed to list threads:", error);
    throw error;
  }
  return (data ?? []) as ChatThreadRecord[];
};

// List archived threads only
export const listArchivedThreads = async (
  userId: string
): Promise<ChatThreadRecord[]> => {
  const { data, error } = await charlie()
    .from("chat_threads")
    .select("*")
    .eq("user_id", userId)
    .eq("archived", true)
    .order("updated_at", { ascending: false });

  if (error) {
    console.error("Failed to list archived threads:", error);
    throw error;
  }
  return (data ?? []) as ChatThreadRecord[];
};

// Create a new thread
export const createThread = async (
  params: CreateThreadParams
): Promise<ChatThreadRecord> => {
  const { data, error } = await charlie()
    .from("chat_threads")
    .insert({
      user_id: params.user_id,
      org_id: params.org_id ?? null,
      provider: params.provider ?? "knowledge-base",
      kb_id: params.kb_id ?? null,
    })
    .select()
    .single();

  if (error) {
    console.error("Failed to create thread:", error);
    throw error;
  }
  return data as ChatThreadRecord;
};

// Update thread fields (title, provider, kb_id, archived)
export const updateThread = async (
  threadId: string,
  updates: Partial<Pick<ChatThreadRecord, "title" | "provider" | "kb_id" | "archived" | "metadata">>
): Promise<ChatThreadRecord> => {
  const { data, error } = await charlie()
    .from("chat_threads")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", threadId)
    .select()
    .single();

  if (error) {
    console.error("Failed to update thread:", error);
    throw error;
  }
  return data as ChatThreadRecord;
};

// Delete a thread (cascade deletes messages)
export const deleteThread = async (threadId: string): Promise<void> => {
  const { error } = await charlie()
    .from("chat_threads")
    .delete()
    .eq("id", threadId);

  if (error) {
    console.error("Failed to delete thread:", error);
    throw error;
  }
};

// Archive/unarchive a thread
export const archiveThread = async (threadId: string): Promise<void> => {
  await updateThread(threadId, { archived: true });
};

export const unarchiveThread = async (threadId: string): Promise<void> => {
  await updateThread(threadId, { archived: false });
};

// Get all messages for a thread, ordered chronologically
export const getThreadMessages = async (
  threadId: string
): Promise<ChatMessageRecord[]> => {
  const { data, error } = await charlie()
    .from("chat_messages")
    .select("*")
    .eq("thread_id", threadId)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Failed to get thread messages:", error);
    throw error;
  }
  return (data ?? []) as ChatMessageRecord[];
};

// Append a message to a thread
export const appendMessage = async (
  params: CreateMessageParams
): Promise<ChatMessageRecord> => {
  const { data, error } = await charlie()
    .from("chat_messages")
    .insert({
      thread_id: params.thread_id,
      role: params.role,
      content: params.content,
      raw_content: params.raw_content ?? null,
      sources: params.sources ?? null,
      provider: params.provider ?? null,
      skill_applied: params.skill_applied ?? false,
      metadata: params.metadata ?? {},
    })
    .select()
    .single();

  if (error) {
    console.error("Failed to append message:", error);
    throw error;
  }
  return data as ChatMessageRecord;
};

// Generate a title for a thread from messages (calls backend)
export const generateThreadTitle = async (
  messages: Array<{ role: string; content: string }>
): Promise<string> => {
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`;
    }

    const response = await fetch(`${API_URL}/api/charlie-chat/threads/generate-title`, {
      method: "POST",
      headers,
      body: JSON.stringify({ messages }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.title || "New conversation";
  } catch (error) {
    console.error("Failed to generate thread title:", error);
    // Fallback: use first 50 chars of first user message
    const firstUserMsg = messages.find((m) => m.role === "user");
    return firstUserMsg
      ? firstUserMsg.content.slice(0, 50) + (firstUserMsg.content.length > 50 ? "..." : "")
      : "New conversation";
  }
};

// Update thread provider
export const updateThreadProvider = async (
  threadId: string,
  provider: ChatProvider,
  kbId?: string | null
): Promise<void> => {
  await updateThread(threadId, { provider, kb_id: kbId ?? null });
};
